// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import dayjs from 'dayjs';
import { getArrayFromCsvString } from '../utils';
import type { PublishedAttributeGroupingFormatted } from '../types';
import { OUTPUT_CATEGORY_RISK_SCORING, OUTPUT_CATEGORY_SESSION_RISK_SCORING } from '../constants';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import { LowRangeOption, HighRangeOption } from 'flow/service/actions';
import { getOutputValueRangeOptions } from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/formHelpers';

export const MIN_MAX_EXCLUDED_ERROR = 'Default Value must fall between Min and Max Accepted Values';
export const VALUES_SET_EXCLUDED_ERROR = 'Default Value must be included in Accepted Values';
export const VALUES_SET_EMPTY_ERROR = 'Default Value cannot be empty string if Accepted Values are entered';

export const isRiskScoringCategory = (category: string) => (
  category === OUTPUT_CATEGORY_RISK_SCORING || category === OUTPUT_CATEGORY_SESSION_RISK_SCORING
);

export const emptyFormState = {
  attributeName: '',
  attributeToken: '',
  objectType: 'entity',
  dataType: '',
  defaultValue: '',
  defaultValueNote: '',
  description: '',
  valueExpiresInDays: '',
  jqFilter: '',
  containsPii: false,
  hasExpiration: false,
  hasJqFilter: false,
  validValueMin: '',
  validValueMax: '',
  validValues: '',
  agentCanCreateValue: true,
  groupingToken: '',
  category: '',
  defineValueRanges: false,
  valueRangeConfig: [],
  valueRangeConfigOptions: {
    lowRange: LowRangeOption.bounded,
    highRange: HighRangeOption.bounded,
  },
};

export const getPopulatedFormState = existingAttribute => {
  const populatedState = cloneDeep(emptyFormState);
  Object.keys(populatedState).forEach(attributeKey => {
    if (typeof populatedState[attributeKey] === 'boolean') {
      if (attributeKey === 'hasExpiration') populatedState[attributeKey] = !!existingAttribute?.valueExpiresInDays;
      if (attributeKey === 'hasJqFilter') {
        /*
        If populating form for published attribute, need to infer hasJqFilter from presence of jqFilter string.
        Otherwise can use dedicated "use_jq" workflow action option value
        */
        populatedState[attributeKey] = existingAttribute?.attributeToken
          ? !!existingAttribute?.jqFilter
          : existingAttribute?.hasJqFilter;
      }
      if (attributeKey === 'containsPii') populatedState[attributeKey] = !!existingAttribute[attributeKey];
      if (attributeKey === 'agentCanCreateValue') populatedState[attributeKey] = !!existingAttribute[attributeKey];
      if (attributeKey === 'defineValueRanges') {
        // Set to true if category requires value ranges or if valueRangeConfig exists (previously configured)
        populatedState[attributeKey] = Boolean(
          isRiskScoringCategory(existingAttribute?.category)
          || existingAttribute?.valueRangeConfig?.length > 0,
        );
      }
    } else if (attributeKey === 'valueRangeConfig') {
      populatedState[attributeKey] = existingAttribute[attributeKey] ?? [];
    } else if (attributeKey === 'valueRangeConfigOptions') {
      // valueRangeConfigOptions is not stored in the DB and needs to be populated based on config
      populatedState[attributeKey] = getOutputValueRangeOptions({
        outputValueRangeOptions: undefined, // undefined because we know options don't exist on the attribute
        outputValueRange: existingAttribute.valueRangeConfig ?? [],
      });
    } else {
      populatedState[attributeKey] = get(existingAttribute, attributeKey, '');
    }
  });
  return populatedState;
};

export const attributeKeyMap = {
  attributeName: 'published_attribute_name',
  attributeToken: 'published_attribute_token',
  dataType: 'data_type',
  defaultValue: 'default_value',
  defaultValueNote: 'default_value_note',
  description: 'description',
  valueExpiresInDays: 'value_expires_in',
  containsPii: 'contains_pii',
  jqFilter: 'jq_filter',
};

export const isInputOutsideLimitsNum = (
  input: string,
  limitLow: string,
  limitHigh: string,
  dataType: 'integer' | 'decimal',
): boolean => {
  if (!input) return false;

  if (dataType === 'integer') {
    const inputInt = parseInt(input, 10);
    return (
      (limitLow && (inputInt < parseInt(limitLow, 10)))
      || (limitHigh && (inputInt > parseInt(limitHigh, 10)))
    );
  }

  if (dataType === 'decimal') {
    const inputFloat = parseFloat(input);
    return (
      (limitLow && (inputFloat < parseFloat(limitLow)))
      || (limitHigh && (inputFloat > parseFloat(limitHigh)))
    );
  }

  return false;
};

export const isInputOutsideLimitsDate = (
  input: string,
  limitLow: string,
  limitHigh: string,
): boolean => {
  if (!input) return false;

  return (
    (limitLow && dayjs(input).isBefore(dayjs(limitLow)))
    || (limitHigh && dayjs(input).isAfter(dayjs(limitHigh)))
  );
};

export const getDefaultValueNumberError = (value, validValueMin, validValueMax, dataType) => {
  const isOutsideLimits = (
    isInputOutsideLimitsNum(value, validValueMin, validValueMax, dataType)
  );
  if (isOutsideLimits) {
    return MIN_MAX_EXCLUDED_ERROR;
  }
  return null;
};

export const getDefaultValueDateError = (value, validValueMin, validValueMax) => {
  const isOutsideLimits = (
    isInputOutsideLimitsDate(value, validValueMin, validValueMax)
  );
  if (isOutsideLimits) {
    return MIN_MAX_EXCLUDED_ERROR;
  }
  return null;
};

export const getDefaultValueStringError = (value, validValues) => {
  const validValuesArray = getArrayFromCsvString(validValues);

  if (!validValuesArray.length) {
    return null;
  }

  if (!value) {
    return VALUES_SET_EMPTY_ERROR;
  }

  if (!validValuesArray.includes(value)) {
    return VALUES_SET_EXCLUDED_ERROR;
  }

  return null;
};

export const getGroupingFormDefaultValues = (
  groupingToEdit: PublishedAttributeGroupingFormatted,
) => ({
  groupingName: groupingToEdit.groupingName,
  groupingOrder: groupingToEdit.groupingOrder,
  groupingAttributes: (
    groupingToEdit.publishedAttributes.map(attribute => ({
      label: attribute.name,
      value: attribute.token,
    }))
  ),
});

export const getMaximumGroupingOrderValue = (
  publishedAttributeGroupings: PublishedAttributeGroupingFormatted[],
) => {
  if (!publishedAttributeGroupings) {
    return null;
  }
  const groupingsWithOrders = publishedAttributeGroupings.filter(grouping => (
    grouping.groupingOrder !== null
  ));

  return groupingsWithOrders.length + 1;
};

export function validateValueRanges(
  value: OutputValueRange[],
  rangeOptions: ValueRangeConfigOptions,
): string | true {
  const { lowRange, highRange } = rangeOptions;
  const isLowUnbounded = lowRange === LowRangeOption.unbounded;
  const isHighUnbounded = highRange === HighRangeOption.unbounded;

  let hasInvalidRange = false;
  let hasMissingLabel = false;
  let hasMissingColor = false;
  let currentRow = 0;

  while (currentRow < value.length && !(hasInvalidRange && hasMissingLabel && hasMissingColor)) {
    const {
      minValue, maxValue, label, color,
    } = value[currentRow];
    if (!label || label.trim() === '') {
      hasMissingLabel = true;
    }
    if (!color) {
      hasMissingColor = true;
    }
    const isMinValid = (
      // first row with low unbounded should not have a minValue
      (currentRow === 0 && isLowUnbounded && minValue === null)
      // first row with bounded low should have a minValue
      || (currentRow === 0 && !isLowUnbounded && minValue !== null && minValue !== undefined)
      || (
        currentRow > 0
        && minValue !== null
        && minValue !== undefined
        && minValue === value[currentRow - 1].maxValue
      )
    );
    const isMaxValid = (
      // last row with high unbounded should not have a maxValue
      (currentRow === value.length - 1 && isHighUnbounded && maxValue === null)
      // last row with bounded high should have a maxValue AND maxValue >= minValue
      || (
        currentRow === value.length - 1
        && !isHighUnbounded
        && maxValue !== null
        && maxValue !== undefined
        && (minValue === null || maxValue >= minValue)
      )
      || (
        currentRow < value.length - 1
        && maxValue !== null
        && maxValue !== undefined
        && (minValue === null || maxValue >= minValue)
      )
    );
    if (!isMinValid || !isMaxValid) {
      hasInvalidRange = true;
    }
    currentRow += 1;
  }

  if (hasInvalidRange && hasMissingLabel && hasMissingColor) {
    return 'All ranges must have a label, color, and valid range.';
  }

  if (hasMissingLabel || hasMissingColor || hasInvalidRange) {
    const errors = [];
    if (hasInvalidRange) errors.push('valid ranges');
    if (hasMissingLabel) errors.push('a label');
    if (hasMissingColor) errors.push('a color');

    return `All value range rows must have ${errors.join(' and ')}.`;
  }

  return true;
}

export default {
  emptyFormState,
  getPopulatedFormState,
  attributeKeyMap,
  isInputOutsideLimitsNum,
  isInputOutsideLimitsDate,
  getDefaultValueNumberError,
  getDefaultValueDateError,
  getDefaultValueStringError,
  getGroupingFormDefaultValues,
  getMaximumGroupingOrderValue,
  validateValueRanges,
};
